import React, { useState, useEffect, useContext } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Button,
  Alert,
  Spinner,
} from 'reactstrap';
import { FirebaseContext } from '../../components/Firebase';
import { useAuthStore } from '../../store/auth.store';
import { useOrganizationStore } from '../../store/organization.store';
import { useToast } from '../../contexts/ToastContext';
import * as ROUTES from '../../constants/routes';

interface InviteData {
  code: string;
  orgId: string;
  orgName: string;
  createdBy: string;
  createdAt: string;
  expiresAt: string | null;
  used: boolean;
  usedBy: string | null;
  usedAt: string | null;
  role: 'USER' | 'OPERATOR' | 'ADMIN';
  invitedEmail: string;
}

const Invite: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const history = useHistory();
  const firebase = useContext(FirebaseContext);
  const auth = useAuthStore(state => state.auth);
  const { showSuccess } = useToast();

  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invite, setInvite] = useState<InviteData | null>(null);
  const [alreadyMember, setAlreadyMember] = useState(false);
  const [emailMismatch, setEmailMismatch] = useState(false);

  const setOrganizations = useOrganizationStore(state => state.setOrganizations);
  const setCurrentOrganization = useOrganizationStore(state => state.setCurrentOrganization);
  const organizations = useOrganizationStore(state => state.organizations);

  // Validate the invite code on mount and when auth changes
  useEffect(() => {
    const validateInvite = async () => {
      if (!firebase || !code) {
        setError('Invalid invite link');
        setLoading(false);
        return;
      }

      try {
        // Pass user email if authenticated for email match check
        const userEmail = auth?.email || undefined;
        const validation = await firebase.validateInvite(code, userEmail);

        if (!validation.valid) {
          // Check if it's an email mismatch error
          if (validation.error === 'This invite was sent to a different email address') {
            setEmailMismatch(true);
            setInvite(validation.invite as InviteData);
          } else {
            setError(validation.error || 'Invalid invite');
          }
          setLoading(false);
          return;
        }

        setInvite(validation.invite as InviteData);
        setEmailMismatch(false);

        // Check if user is already a member of this org
        if (auth) {
          const isMember = organizations.some(org => org.orgId === validation.invite?.orgId);
          setAlreadyMember(isMember);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to validate invite');
      } finally {
        setLoading(false);
      }
    };

    validateInvite();
  }, [firebase, code, auth, organizations]);

  // Handle joining the organization
  const handleJoinOrganization = async () => {
    if (!firebase || !auth || !invite) return;

    setJoining(true);
    setError(null);

    try {
      // Use the invite (passing email for validation)
      const inviteResult = await firebase.useInvite(code, auth.uid, auth.email);

      // Refresh user's organizations
      const updatedOrgs = await firebase.getUserOrganizations(auth.uid);
      setOrganizations(updatedOrgs);

      // Set this as the current organization (include type and category from invite result)
      setCurrentOrganization(invite.orgId, invite.orgName, invite.role, inviteResult.orgType, inviteResult.orgCategory);

      // Show success toast
      showSuccess(`Successfully joined "${invite.orgName}"!`);

      // Redirect to the library
      history.push(ROUTES.FIND_AUDIO);
    } catch (err: any) {
      setError(err.message || 'Failed to join organization');
      setJoining(false);
    }
  };

  // Handle redirect to sign in (preserving invite code)
  const handleSignIn = () => {
    // Store invite code in sessionStorage for after login
    sessionStorage.setItem('pendingInviteCode', code);
    history.push(ROUTES.SIGN_IN);
  };

  // Handle redirect to sign up (preserving invite code)
  const handleSignUp = () => {
    // Store invite code in sessionStorage for after signup
    sessionStorage.setItem('pendingInviteCode', code);
    history.push(ROUTES.SIGN_UP);
  };

  // Go to library if already a member
  const handleGoToLibrary = () => {
    if (invite) {
      const org = organizations.find(o => o.orgId === invite.orgId);
      setCurrentOrganization(
        invite.orgId,
        invite.orgName,
        org?.role || 'USER',
        org?.orgType || 'artist',
        org?.orgCategory
      );
    }
    history.push(ROUTES.FIND_AUDIO);
  };

  // Handle sign out to use different account
  const handleSignOut = async () => {
    if (!firebase) return;
    await firebase.doSignOut();
    // Page will re-render with auth = null
  };

  if (loading) {
    return (
      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col md={6} className="text-center">
            <Spinner color="primary" />
            <p className="text-white mt-3">Validating invite...</p>
          </Col>
        </Row>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col md={6}>
            <Card>
              <CardBody className="text-center">
                <CardTitle tag="h3" className="text-white mb-4">
                  Invalid Invite
                </CardTitle>
                <Alert color="danger">{error}</Alert>
                <p className="text-muted">
                  This invite link may have expired or already been used.
                </p>
                <Button color="primary" onClick={() => history.push(ROUTES.SIGN_IN)}>
                  Go to Sign In
                </Button>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  if (!invite) {
    return null;
  }

  // Email mismatch - user is signed in with wrong email
  if (emailMismatch && auth) {
    return (
      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col md={6}>
            <Card>
              <CardBody className="text-center">
                <CardTitle tag="h3" className="text-white mb-4">
                  Wrong Account
                </CardTitle>

                <Alert color="warning">
                  This invite was sent to <strong>{invite.invitedEmail}</strong>
                </Alert>

                <p className="text-muted mb-4">
                  You're currently signed in as <strong>{auth.email}</strong>.
                  <br />
                  Please sign in with the correct account to accept this invite.
                </p>

                <div className="d-grid gap-2">
                  <Button color="primary" onClick={handleSignOut}>
                    Sign Out & Use Different Account
                  </Button>
                  <Button color="outline-light" onClick={() => history.push(ROUTES.FIND_AUDIO)}>
                    Go to Library
                  </Button>
                </div>

                <hr className="my-4" style={{ borderColor: 'rgba(255,255,255,0.1)' }} />

                <p className="text-muted small mb-0">
                  Invite for: {invite.orgName}
                </p>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="mt-5">
      <Row className="justify-content-center">
        <Col md={6}>
          <Card>
            <CardBody className="text-center">
              <CardTitle tag="h3" className="text-white mb-4">
                You're Invited!
              </CardTitle>

              <div className="mb-4">
                <p className="text-muted mb-1">You've been invited to join</p>
                <h4 className="text-white">{invite.orgName}</h4>
                {invite.invitedEmail && (
                  <p className="text-muted small mt-2">
                    Invite for: {invite.invitedEmail}
                  </p>
                )}
              </div>

              {alreadyMember ? (
                <>
                  <Alert color="info">
                    You're already a member of this organization.
                  </Alert>
                  <Button color="primary" onClick={handleGoToLibrary}>
                    Go to Library
                  </Button>
                </>
              ) : auth ? (
                <>
                  <p className="text-muted mb-4">
                    Click below to join this organization as a <strong>{invite.role}</strong>.
                  </p>
                  <Button
                    className="btn-success-custom"
                    size="lg"
                    onClick={handleJoinOrganization}
                    disabled={joining}
                    style={{ position: 'relative', zIndex: 2 }}
                  >
                    <span style={{ position: 'relative', zIndex: 3 }}>
                      {joining ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Joining...
                        </>
                      ) : (
                        'Join Organization'
                      )}
                    </span>
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-muted mb-4">
                    Sign in or create an account with <strong>{invite.invitedEmail}</strong> to join this organization.
                  </p>
                  <div className="d-flex flex-column align-items-center gap-2">
                    <Button
                      className="btn-info-custom"
                      size="lg"
                      onClick={handleSignIn}
                      style={{ position: 'relative', zIndex: 2, minWidth: '160px' }}
                    >
                      <span style={{ position: 'relative', zIndex: 3 }}>Sign In</span>
                    </Button>
                    <Button color="outline-light" onClick={handleSignUp}>
                      Create Account
                    </Button>
                  </div>
                </>
              )}

              <hr className="my-4" style={{ borderColor: 'rgba(255,255,255,0.1)' }} />

              <p className="text-muted small mb-0">
                Invite expires: {invite.expiresAt
                  ? new Date(invite.expiresAt).toLocaleDateString()
                  : 'Never'}
              </p>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Invite;
